import { html, render, nothing, type TemplateResult } from 'lit-html';
import styles from './pk-escrow-recover-modal.css?inline';
import { decryptBlob, type EscrowParams } from '../../crypto/recovery-crypto.js';
import { requestThroughGate, sendAction, stepUpDemand, type StepUpOutcome } from '../../lib/step-up.js';
import '../pk-alert/pk-alert.js';
import '../pk-button/pk-button.js';
import '../pk-modal/pk-modal.js';
import '../pk-secret-reveal/pk-secret-reveal.js';
import type { PkSecretReveal } from '../pk-secret-reveal/pk-secret-reveal.js';

/** The one message for a wrong password **and** a tampered copy (the crypto cannot tell them apart; neither should we). */
export const RECOVER_FAILED_MESSAGE = 'That recovery password did not unlock the key, or the escrow copy was altered.';

/**
 * `<pk-escrow-recover-modal>` — recovers an escrowed private key **in the browser**.
 *
 * The trigger downloads the escrow copy from `download-url` (a POST that answers `{ encryptedBlob, escrowParams }`
 * with `Cache-Control: no-store`). The API gates it behind a fresh TOTP, so inside a `<pk-step-up-gate>` the
 * download goes through the gate's prompt first. Then a modal asks for the recovery password, `decryptBlob` runs
 * here, and the key is shown once through `<pk-secret-reveal>` (copy / download / wipe). A wrong password and a
 * tampered copy get the same message. Closing the modal wipes the ciphertext and the key from the page.
 *
 * Attributes:
 *   - `download-url`  — POST URL of the escrow copy. Required.
 *   - `key-id`        — the key (download file name, labels).
 *   - `filename`      — file name of the recovered key (default `{key-id}.pem`).
 *   - `trigger-label` — trigger button text (default `Recover the key with your password`).
 *   - `csrf-token` / `csrf-name` — anti-forgery field of the POST.
 *
 * Events:
 *   - `pk-escrow-recovered`      — `{ keyId }` after a successful decrypt (never carries the key).
 *   - `pk-escrow-recover-failed` — `{ keyId, reason: 'download' | 'decrypt' }`.
 */
export class PkEscrowRecoverModal extends HTMLElement {
    static readonly tagName = 'pk-escrow-recover-modal';

    static get observedAttributes(): string[] {
        return ['download-url', 'key-id', 'filename', 'trigger-label', 'csrf-token', 'csrf-name'];
    }

    private readonly _root: ShadowRoot;
    private _open = false;
    private _busy = false;
    private _error = '';
    private _blob: { encryptedBlob: string; escrowParams: EscrowParams } | null = null;
    private _recovered = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._root.addEventListener('submit', this._onSubmit);
        this._render();
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._onSubmit);
        this._wipe();
    }

    attributeChangedCallback(): void {
        if (this.isConnected) this._render();
    }

    /** True while the password modal is open. */
    get open(): boolean {
        return this._open;
    }

    private get _keyId(): string {
        return this.getAttribute('key-id') ?? '';
    }

    /** Downloads the escrow copy (through the gate when there is one) and opens the password modal. */
    async start(): Promise<void> {
        const url = this.getAttribute('download-url') ?? '';
        if (!url || this._busy) return;
        this._error = '';
        this._busy = true;
        this._render();

        const fields: Record<string, string> = {};
        const token = this.getAttribute('csrf-token') ?? '';
        if (token) fields[this.getAttribute('csrf-name') ?? '__RequestVerificationToken'] = token;
        const action = { url, fields, label: `Download the escrow copy of ${this._keyId}` };
        const outcome: StepUpOutcome = await (requestThroughGate(this, action) ?? this._direct(action));

        this._busy = false;
        const blob = outcome.data?.['encryptedBlob'];
        const params = outcome.data?.['escrowParams'];
        if (outcome.ok && typeof blob === 'string' && params && typeof params === 'object') {
            this._blob = { encryptedBlob: blob, escrowParams: params as EscrowParams };
            this._recovered = false;
            this._open = true;
        } else if (!outcome.cancelled) {
            this._error = typeof outcome.data?.['message'] === 'string' && outcome.data['message']
                ? outcome.data['message'] as string
                : 'The escrow copy could not be downloaded.';
            this._fail('download');
        }
        this._render();
    }

    private async _direct(action: { url: string; fields: Record<string, string> }): Promise<StepUpOutcome> {
        const response = await sendAction(action, null);
        const ok = response.status >= 200 && response.status < 300;
        const demand = stepUpDemand(response);
        return {
            ok, status: response.status, data: response.data, cancelled: false,
            ...(ok ? {} : { error: demand.required ? 'step-up-required' : String(response.data?.['error'] ?? 'unavailable') }),
        };
    }

    private readonly _onTrigger = (): void => { void this.start(); };

    private readonly _onSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        if (!form.classList.contains('unlock')) return;
        e.preventDefault();
        void this._unlock();
    };

    private async _unlock(): Promise<void> {
        const input = this._root.querySelector('input[data-field="password"]') as HTMLInputElement | null;
        const password = input?.value ?? '';
        if (!this._blob || !password) return;
        this._busy = true;
        this._error = '';
        this._render();
        try {
            const pem = await decryptBlob(this._blob.encryptedBlob, this._blob.escrowParams, password);
            if (input) input.value = '';
            this._blob = null;
            this._recovered = true;
            this._busy = false;
            this._render();
            const reveal = this._root.querySelector('pk-secret-reveal') as PkSecretReveal | null;
            reveal?.show(pem);
            this.dispatchEvent(new CustomEvent('pk-escrow-recovered', { bubbles: true, composed: true, detail: { keyId: this._keyId } }));
        } catch {
            if (input) input.value = '';
            this._busy = false;
            this._error = RECOVER_FAILED_MESSAGE;
            this._fail('decrypt');
            this._render();
        }
    }

    private _fail(reason: 'download' | 'decrypt'): void {
        this.dispatchEvent(new CustomEvent('pk-escrow-recover-failed', { bubbles: true, composed: true, detail: { keyId: this._keyId, reason } }));
    }

    private _wipe(): void {
        this._blob = null;
        const reveal = this._root.querySelector('pk-secret-reveal') as PkSecretReveal | null;
        if (reveal && !reveal.wiped) reveal.wipe();
    }

    private readonly _onClose = (): void => {
        this._wipe();
        this._open = false;
        this._recovered = false;
        this._error = '';
        this._render();
    };

    private template(): TemplateResult {
        const keyId = this._keyId;
        const filename = this.getAttribute('filename') ?? `${keyId || 'recovered-key'}.pem`;
        return html`
            <style>${styles}</style>
            <pk-button class="trigger" variant="secondary" type="button" ?loading=${this._busy && !this._open} data-submitting-label="Downloading…" @click=${this._onTrigger}>
                ${this.getAttribute('trigger-label') ?? 'Recover the key with your password'}
            </pk-button>
            ${this._error && !this._open ? html`<pk-alert variant="error" class="download-error" role="alert">${this._error}</pk-alert>` : nothing}
            <pk-modal class="recover" ?open=${this._open} title="Recover the private key" @pk-cancel=${this._onClose}>
                ${this._recovered
                    ? html`
                        <pk-secret-reveal class="recovered" label="Recovered private key" filename=${filename} mime="application/x-pem-file"></pk-secret-reveal>
                        <pk-button slot="primary" class="close" variant="primary" type="button" @click=${this._onClose}>Close and wipe</pk-button>
                    `
                    : html`
                        <p class="lede">The escrow copy is unlocked here, in your browser, with your recovery password. We cannot unlock it for you.</p>
                        ${this._error ? html`<pk-alert variant="error" class="unlock-error" role="alert">${this._error}</pk-alert>` : nothing}
                        <form class="unlock" autocomplete="off">
                            <label class="field">
                                <span>Recovery password</span>
                                <input data-field="password" type="password" autocomplete="off" />
                            </label>
                            <pk-button class="unlock-submit" variant="primary" type="submit" ?loading=${this._busy} data-submitting-label="Unlocking…">Unlock</pk-button>
                        </form>
                    `}
            </pk-modal>
        `;
    }

    private _render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkEscrowRecoverModal.tagName)) {
    customElements.define(PkEscrowRecoverModal.tagName, PkEscrowRecoverModal);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-escrow-recover-modal': PkEscrowRecoverModal;
    }
}
